<template>
  <div class="relative">
    <Listbox as="div" v-model="selected" :disabled="readonly || disabled">
      <!-- unused -->
      <div v-show="title" class="mb-2.5">
        <ListboxLabel class="font-inter-heading-18 text-content-basic-1 flex items-start gap-1">
          {{ title }}
          <div class="w-[4px] h-[4.05px] bg-accent-blue-3 rounded-full"></div>
        </ListboxLabel>
      </div>

      <div class="relative">
        <!-- 드롭다운 버튼 -->
        <ListboxButton
          :disabled="readonly || disabled"
          :class="[dropdownButtonClass, 'flex items-center justify-between']"
        >
          <span
            class="truncate flex-1"
            :class="placeholderTextClass"
          >
            {{ selected ? selected.text : placeholder }}
          </span>
          <span class="flex-shrink-0 flex items-center pl-2 pointer-events-none">
            <ArrowDropdownIcon :width="9" :height="6" weight="THIN" color="text-content-basic-3" />
          </span>
        </ListboxButton>

        <!-- 드롭다운 옵션 -->
        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
          class="bg-background-basic-1"
        >
          <ListboxOptions :class="[dropdownOptionsClass, 'ds-scrollbar']" class="bg-background-basic-1">
            <ListboxOption
              as="template"
              v-for="item in selectOption"
              :key="item.value"
              :value="item.value"
              v-slot="{ active }"
              class="bg-background-basic-1"
            >
              <li class="cursor-pointer select-none relative">
                <div
                  :class="[
                    'px-2 py-3 flex align-center rounded-2',
                    active ? 'bg-background-basic-3' : 'bg-background-basic-1',
                  ]"
                >
                  <span class="font-body-sm-regular whitespace-normal break-words line-clamp-2">
                    {{ item.text }}
                  </span>
                </div>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>

    <p v-show="error && showErrorText" class="mt-1 font-caption-md-regular text-accent-red-3">
      {{ error }}
    </p>
  </div>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import ArrowDropdownIcon from '@/components/design-system/icon/arrow-drop-down.vue'

export default {
  components: {
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    ArrowDropdownIcon,
  },
  props: {
    modelValue: [String, Object, Number],
    title: String,
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [{ text: null, value: null }],
    },
    placeholder: {
      type: String,
      default: 'Select an Option',
    },
    useDefaultMsg: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'LG',
      validator: (val) => ['SM', 'MD', 'LG'].includes(val.toUpperCase()),
    },
    width: { type: String, default: 'w-[10rem]' },
    dropdownAlign: {
      type: String,
      default: 'left',
      validator: (v) => ['left', 'right'].includes(v),
    },
    error: { type: [String, Boolean], default: null },
    showErrorText: { type: Boolean, default: true },
  },
  emits: ['change', 'update:modelValue'],
  data() {
    return {
      buttonClassMap: {
        LG: 'h-[2.75rem] px-3 relative border border-border-basic-2 rounded-2 text-left focus:border-primary-3',
        MD: 'h-[2.25rem] px-2 relative border border-border-basic-2 rounded-2 text-left focus:border-primary-3',
        SM: 'h-[2rem] px-2 relative border border-border-basic-2 rounded-2 text-left focus:border-primary-3',
      },
      buttonTextMap: {
        LG: 'font-body-sm-regular text-content-basic-1',
        MD: 'font-caption-md-regular text-content-basic-1',
        SM: 'font-caption-md-regular text-content-basic-1',
      },
      optionsClass:
        'absolute px-2 py-2 z-10 top-full mt-2 min-w-[16rem] max-h-[16rem] overflow-y-auto bg-background-basic-1 border border-border-basic-2 rounded-2',
      optionsTextClass: 'font-body-sm-regular text-content-basic-1',
    }
  },
  computed: {
    widthClass() {
      return this.width
    },
    selectOption() {
      const option = [...this.options]
      if (this.useDefaultMsg) {
        option.unshift({ text: this.placeholder, value: null })
      }
      return option
    },
    selected: {
      get() {
        if (!this.modelValue && this.useDefaultMsg) return this.selectOption[0]

        const isInclude = this.valueArray.some((i) => i === this.modelValue)
        if (this.modelValue && !isInclude) {
          this.$emit('update:modelValue', this.valueArray[0])
        }

        return this.selectOption.find((item) => item.value === this.modelValue)
      },
      set(value) {
        this.$emit('update:modelValue', value)
        this.$emit('change')
      },
    },
    valueArray() {
      return this.options
        .filter((option) => option.value != null)
        .map((option) => option.value)
    },
    dropdownButtonClass() {
      const widthClass = this.widthClass
      const size = this.size.toUpperCase()
      const buttonClass = this.buttonClassMap[size] || ''
      const textClass = this.buttonTextMap[size] || ''

      let stateClass = ''

      if (this.disabled) {
        stateClass = 'bg-background-basic-3 cursor-not-allowed text-content-basic-3'
      } else if (this.readonly) {
        stateClass = 'bg-background-basic-3 cursor-default'
      } else {
        stateClass = 'bg-background-basic-1 cursor-pointer'
      }

      const errorClass = this.error && !this.disabled
        ? 'border-accent-red-3'
        : ''

      return [widthClass, buttonClass, textClass, stateClass, errorClass].join(' ')
    },
    placeholderTextClass() {
      if (this.disabled) {
        return 'text-content-basic-3'
      }

      return {
        'text-content-basic-3': !this.modelValue,
      }
    },
    dropdownOptionsClass() {
      const widthClass = this.widthClass
      const optionsClass = this.optionsClass
      const optionsTextClass = this.optionsTextClass
      const positionClass = this.dropdownAlign === 'right'
        ? 'right-0'
        : 'left-0'
      return [positionClass, widthClass, optionsClass, optionsTextClass].join(' ')
    },
  },
  methods: {},
}
</script>